import { Link, useParams } from "react-router-dom";
import { workshopBySlug } from "../../../content";
import type { Workshop } from "../../../types/content";
import NotFound from "../NotFound/NotFound";
import CertificateShowcase from "./CertificateShowcase";
import WorkshopGallery from "./WorkshopGallery";
import { monthLabel } from "./Workshops";

/**
 * ============================================================================
 * One workshop — the /workshops/:slug detail route (D-081).
 * ============================================================================
 *
 * Direct consumer of the content layer (no bridge, V.4). To change what any of
 * these pages says, edit `src/content/data/workshops.json` — not this file.
 * There is one component and three routes; nothing here is per-workshop.
 *
 * ----------------------------------------------------------------------------
 * THE UNKNOWN SLUG IS A REAL BRANCH, NOT A DEFENSIVE ONE
 * ----------------------------------------------------------------------------
 * `/workshops/anything-else` never reaches this component on a cold load:
 * Firebase matches no file and no rewrite, and serves `404.html` with a real
 * 404 status (D-045). But react-router matches `:slug` against ANY segment, so
 * a client-side navigation — a mistyped in-app link, a stale bookmark followed
 * from an already-loaded page — arrives here with a slug the layer does not
 * have. Rendering the 404 page is the honest answer, and it renders the same
 * document a cold load would have got.
 *
 * ----------------------------------------------------------------------------
 * THE CERTIFICATE SHOWCASE IS THE SAME COMPONENT THE INDEX USES
 * ----------------------------------------------------------------------------
 * Not a copy of it. The one entry with a credential renders the identical
 * verified path on both routes, including the two-key gate that decides whether
 * a picture appears at all (`src/utils/workshopImages.ts`). A second
 * implementation of that gate is exactly the D-065 defect — eligibility and
 * rendering decided in two files with only one of them tested.
 */

/**
 * The page's standfirst, and it is a FUNCTION rather than a constant.
 *
 * Every other route in this app exports a fixed `*_STANDFIRST` string, because
 * every other route is one page. This one is three, and each needs its own
 * meta description — a shared sentence across three detail pages would put them
 * in competition for the same query with the same words, which is the exact
 * defect `src/seo/routeMeta.ts` was written to remove.
 *
 * IT IS BUILT FROM THE ENTRY'S OWN FIELDS AND ASSERTS NOTHING ELSE. No count,
 * no span, no adjective. D-066 is the record of a standfirst that opened "two
 * decades" against a three-year record and shipped that in a `<meta
 * name="description">`, which is the one line on a page that nothing checks.
 */
export const workshopStandfirst = (workshop: Workshop): string =>
	`${workshop.role} at ${workshop.host}, ${monthLabel(workshop.date)}. ${workshop.description}`;

export default function WorkshopDetail(): JSX.Element {
	const { slug } = useParams<{ slug: string }>();
	const workshop = slug === undefined ? undefined : workshopBySlug(slug);

	if (workshop === undefined) return <NotFound />;

	const when = workshop.dateDetail
		? `${monthLabel(workshop.date)} (${workshop.dateDetail})`
		: monthLabel(workshop.date);

	return (
		<section className="section-shell" aria-labelledby="workshop-heading">
			<div className="section-rail">
				{/* THE BREADCRUMB IS THE WAY BACK, AND IT IS FIRST ON PURPOSE.
				    A detail page reached from a search result has no history to
				    go back through, so "back" has to be a link on the page
				    rather than a button in the browser. */}
				<nav aria-label="Breadcrumb">
					<Link
						to="/workshops"
						className="inline-flex min-h-[24px] items-center text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						<span aria-hidden="true">←&nbsp;</span>Workshops &amp; Training
					</Link>
				</nav>

				<p className="section-eyebrow mt-6">Teaching &amp; Enablement</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="workshop-heading" className="heading">
					{workshop.title}
				</h1>

				<div className="mt-4 flex flex-wrap items-center gap-3">
					<span className="pill-tint tabular-nums border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]">
						{when}
					</span>
					<p className="text-sm text-[var(--text-muted)]">
						<span className="font-medium text-[var(--text-primary)]">{workshop.role}</span>
						<span aria-hidden="true"> · </span>
						{workshop.host}
					</p>
				</div>

				<p className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--text-secondary)]">
					{workshop.description}
				</p>

				{workshop.certificate && (
					<CertificateShowcase certificate={workshop.certificate} statement={workshop.statement} />
				)}

				{/* Renders null while the list is empty, which it is on all three
				    entries. See WorkshopGallery — the empty state is the finished
				    state, not a placeholder. */}
				<WorkshopGallery images={workshop.images} workshopTitle={workshop.title} />

				<div className="mt-16 flex flex-wrap justify-center gap-6">
					<Link
						to="/workshops"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						← All workshops
					</Link>
					<Link
						to="/achievements"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						Recognition →
					</Link>
				</div>
			</div>
		</section>
	);
}
